<script setup>
import { computed, onUnmounted, ref, inject, watchEffect } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SynphoneMeetConfirmationModal from '../../SynphoneMeetConfirmationModal.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const [showVideoButton, toggleVideoButton] = useToggle(false);
const [showVoiceButton, toggleVoiceButton] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const phoneNumber = computed(
  () => currentChat.value?.meta?.sender?.phone_number ?? null
);

const route = useRoute();
const getAccountFn = useMapGetter('accounts/getAccount');
const { updateUISettings } = useUISettings();

const accountId = computed(() => {
  return Number(route.params.accountId);
});
const featuresAccount = computed(
  () => getAccountFn.value(accountId.value).features
);

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

watchEffect(() => {
  if (featuresAccount?.value?.synphone_meet === true) {
    toggleVideoButton();
  }
  if (featuresAccount?.value?.synphone_voice === true) {
    toggleVoiceButton();
  }
});

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});

const showSynphoneMeetConfirmPopup = ref(false);

const openSynphoneMeetConfirmPopup = () => {
  showSynphoneMeetConfirmPopup.value = true;
};
const hideSynphoneMeetConfirmPopup = () => {
  showSynphoneMeetConfirmPopup.value = false;
};

const voiceCall = () => {
  store.dispatch('voiceCall/makeCall', phoneNumber.value);
};

const startSynphoneMeetObservable = inject('startSynphoneMeetObservable');
const handleSynphoneMeet = confirm => {
  if (confirm) {
    startSynphoneMeetObservable.value = true;
  }
};

const { uiSettings } = useUISettings();
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const handleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
  });
};
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <Button
      :label="$t('CONVERSATION.SIDEBAR.CONTACT')"
      size="sm"
      icon="i-ph-user-bold"
      @click="handleConversationSidebarToggle"
    />
    <Button
      v-if="showVoiceButton"
      :label="$t('CONVERSATION.HEADER.SYNPHONE_VOICE')"
      size="sm"
      @click="voiceCall"
    />
    <Button
      v-if="showVideoButton"
      :label="$t('CONVERSATION.HEADER.SYNPHONE_MEET')"
      size="sm"
      @click="openSynphoneMeetConfirmPopup"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
  <woot-modal
    v-model:show="showSynphoneMeetConfirmPopup"
    :on-close="hideSynphoneMeetConfirmPopup"
  >
    <SynphoneMeetConfirmationModal
      @close="hideSynphoneMeetConfirmPopup"
      @confirm="handleSynphoneMeet"
    />
  </woot-modal>
</template>
